import * as Sentry from '@sentry/browser'
import { useCallback, useRef } from 'react'

/**
 * Tracks an async operation and reports errors to Sentry with context
 *
 * @param operation - The async function to execute
 * @param context - Context about the operation for error reporting
 * @returns The result of the operation
 *
 * @example
 * const data = await trackAsync(
 *   () => fetchUserData(userId),
 *   {
 *     operation: 'fetchUserData',
 *     userId,
 *     description: 'Loading user profile data'
 *   }
 * )
 */
export async function trackAsync<T>(
  operation: () => Promise<T>,
  context: {
    operation: string
    description?: string
    [key: string]: unknown
  },
): Promise<T> {
  // Add breadcrumb for operation start
  Sentry.addBreadcrumb({
    message: `Starting ${context.operation}`,
    category: 'async.operation',
    level: 'info',
    data: context,
  })

  try {
    const result = await operation()

    // Add success breadcrumb
    Sentry.addBreadcrumb({
      message: `Completed ${context.operation}`,
      category: 'async.operation',
      level: 'info',
      data: { ...context, success: true },
    })

    return result
  } catch (error) {
    // Add error breadcrumb
    Sentry.addBreadcrumb({
      message: `Failed ${context.operation}`,
      category: 'async.operation',
      level: 'error',
      data: { ...context, success: false },
    })

    // Report to Sentry with context
    Sentry.withScope((scope) => {
      scope.setContext('async_operation', context)
      scope.setTag('operation', context.operation)
      scope.setLevel('error')

      // Add any additional context fields as extras
      Object.entries(context).forEach(([key, value]) => {
        if (key !== 'operation' && key !== 'description') {
          scope.setExtra(key, value)
        }
      })

      Sentry.captureException(error, {
        fingerprint: [context.operation, '{{ default }}'],
      })
    })

    // Re-throw the error so calling code can handle it
    throw error
  }
}

/**
 * React hook that wraps an async callback with error tracking
 * Useful for event handlers and callbacks that perform async operations
 *
 * The callback itself should be memoized with useCallback if needed.
 *
 * @param callback - The async callback to wrap
 * @param context - Context about the operation for error reporting
 * @returns A wrapped callback that tracks errors
 *
 * @example
 * const handleSubmit = usePortalCallback(
 *   async (data: FormData) => {
 *     await api.submitForm(data)
 *   },
 *   { operation: 'submitForm', form: 'userProfile' }
 * )
 */
export function usePortalCallback<TArgs extends unknown[], TReturn>(
  callback: (...args: TArgs) => Promise<TReturn>,
  context: {
    operation: string
    description?: string
    [key: string]: unknown
  },
): (...args: TArgs) => Promise<TReturn> {
  const contextRef = useRef(context)
  contextRef.current = context

  return useCallback(
    async (...args: TArgs) => {
      return trackAsync(() => callback(...args), {
        ...contextRef.current,
        args: args.length === 1 ? args[0] : args,
      })
    },
    [callback],
  )
}

/**
 * Higher-order function that wraps an async function with error tracking
 * Useful for wrapping API client methods or service functions
 *
 * @param fn - The async function to wrap
 * @param operationName - Name of the operation for error reporting
 * @returns A wrapped function that tracks errors
 *
 * @example
 * const trackedFetch = withErrorTracking(
 *   fetchData,
 *   'fetchData'
 * )
 */
export function withErrorTracking<TArgs extends unknown[], TReturn>(
  fn: (...args: TArgs) => Promise<TReturn>,
  operationName: string,
): (...args: TArgs) => Promise<TReturn> {
  return async (...args: TArgs) => {
    return trackAsync(() => fn(...args), {
      operation: operationName,
      args: args.length === 1 ? args[0] : args,
    })
  }
}

/**
 * Captures an error with additional context
 * Useful for catch blocks where you want to add more context
 *
 * @param error - The error to capture
 * @param context - Additional context for the error
 *
 * @example
 * try {
 *   await someOperation()
 * } catch (error) {
 *   captureWithContext(error, {
 *     operation: 'someOperation',
 *     userId: currentUser.id
 *   })
 * }
 */
export function captureWithContext(
  error: unknown,
  context: Record<string, unknown>,
): void {
  Sentry.withScope((scope) => {
    scope.setContext('error_context', context)

    Object.entries(context).forEach(([key, value]) => {
      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        scope.setTag(key, String(value))
      } else {
        scope.setExtra(key, value)
      }
    })

    Sentry.captureException(error)
  })
}
